import type { ReactNode } from 'react';
import { Icon } from '@/components/icon/index.js';
import type { IconName } from '@/components/icon/index.js';
import { Button } from '../design-system/primitives/Button/Button.js';
import { GatorMarkCircle } from './GatorMarkCircle.js';
import { cn } from '../../lib/utils.js';

export type ErrorAction = {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'ghost';
  icon?: IconName;
};

/**
 * The one branded error surface — used by 404, 500 and the router error
 * boundary so every dead end looks intentional and on-brand: a giant ghosted
 * status code watermark, the Gator mascot in a glowing halo with a speech
 * bubble in its voice, then headline, message and actions.
 *
 * Deliberately self-contained (no router/auth imports) so it can render even
 * when the app tree has crashed; navigation is passed in as onClick handlers.
 */
export function ErrorScreen({
  code,
  bubble,
  title,
  message,
  actions,
  detail,
}: {
  code: string;
  bubble: string;
  title: string;
  message: string;
  actions: ErrorAction[];
  detail?: string | null;
}) {
  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-ig-bg-primary px-5 py-16 text-ig-text-primary">
      {/* Ghost status code watermark */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 flex select-none items-center justify-center font-display text-[42vw] font-light leading-none text-ig-text-primary opacity-[0.035] md:text-[340px]"
      >
        {code}
      </span>
      {/* Accent glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/4 h-[420px] w-[560px] -translate-x-1/2 rounded-full bg-ig-accent opacity-[0.07] blur-[120px]"
      />

      <div className="relative flex w-full max-w-xl flex-col items-center text-center">
        {/* Mascot in a glowing halo */}
        <div className="relative flex h-24 w-24 items-center justify-center motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-90 motion-safe:duration-500">
          <span aria-hidden className="absolute inset-0 rounded-full bg-ig-accent/10 blur-xl motion-safe:animate-pulse motion-safe:[animation-duration:3s]" />
          <GatorMarkCircle className="relative h-24 w-24 motion-safe:animate-[float_4s_ease-in-out_infinite]" alt="" />
        </div>

        {/* Speech bubble in the Gator's voice */}
        <div className="mt-5 rounded-lg rounded-bl-sm border border-ig-border bg-ig-bg-secondary px-4 py-2.5 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-500">
          <p className="font-display text-[13px] italic text-ig-text-secondary">{bubble}</p>
        </div>

        <p className="mt-8 font-mono text-[11px] font-medium uppercase tracking-[0.24em] text-ig-accent">
          {code} · {title}
        </p>
        <h1 className="mt-3 font-display text-[38px] font-light leading-[1.05] text-ig-text-primary md:text-[52px]">
          {title}
        </h1>
        <p className="mx-auto mt-4 max-w-md font-sans text-[15px] leading-[1.7] text-ig-text-secondary">{message}</p>

        {detail ? (
          <pre className="mt-5 max-w-full overflow-x-auto rounded-md border border-ig-border bg-ig-bg-secondary px-3 py-2 text-left font-mono text-[11.5px] text-ig-text-tertiary">
            {detail}
          </pre>
        ) : null}

        <div className="mt-9 flex w-full flex-col items-center justify-center gap-3 sm:w-auto sm:flex-row">
          {actions.map((action) => (
            <Button
              key={action.label}
              type="button"
              variant={action.variant ?? 'secondary'}
              size="lg"
              className="h-12 w-full rounded-lg sm:w-auto sm:rounded-md"
              onClick={action.onClick}
              leftIcon={action.icon ? <Icon name={action.icon} className="h-4 w-4" /> : undefined}
            >
              {action.label}
            </Button>
          ))}
        </div>
      </div>
    </main>
  );
}

/** Decorative wrapper kept for future composition. */
export function ErrorScreenDetail({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('mt-5', className)}>{children}</div>;
}
